/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React from 'react';
import Button from '@material-ui/core/Button';
import { Typography } from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogStatus from 'layout/dialog/DialogStatus';
import DialogActions from '@material-ui/core/DialogActions';
import _ from 'lodash';
import { Trans, useTranslation } from 'react-i18next';
import { GetApp } from '@material-ui/icons';
import { isFeatureEnabled, ToggleableFeature } from 'shared/utils/featuresUtils';
import { useConfiguration } from '../../../redux/features/configuration/useConfiguration';
import { ConfigResourceProps } from '../../../redux/enums/ConfigResourceProps';
import { usePaymentDialog } from './usePaymentDialog';
import PaymentDialogState from './enum/PaymentDialogState';
import Loader from '../../../components/loader/Loader';
import { SubmissionStatus } from '../../../models/SubmissionStatus';

export default function PaymentDialog({ submission }) {
  const { t } = useTranslation();

  const globalConfiguration = useConfiguration(ConfigResourceProps.GLOBAL_CONFIGURATION);
  const enablePaymentPdfDownload = isFeatureEnabled(
    globalConfiguration?.resource,
    ToggleableFeature.PAYMENT_PDF_DOWNLOAD,
  );

  const {
    paymentDialogState,
    handleDownloadProofOfPayment,
    handlePaymentPreview,
    handleGoToOverview,
    handleTryAgain,
    handleChangeMethodOfPayment,
    handlePayLater,
  } = usePaymentDialog(submission);

  const hasPaymentPdf =
    enablePaymentPdfDownload &&
    (paymentDialogState === PaymentDialogState.PAYMENT_SUCCESS ||
      paymentDialogState === PaymentDialogState.PAYMENT_FAILED);

  return (
    <Dialog open={!_.isEmpty(paymentDialogState)} fullWidth maxWidth="md">
      <DialogTitle>
        <>
          {paymentDialogState?.hasCheckCircleIcon && <DialogStatus variant="success" />}
          {paymentDialogState?.hasCancelIcon && <DialogStatus variant="error" />}
          {paymentDialogState?.hasWarningIcon && <DialogStatus variant="warning" />}
          <Typography component="h2" variant="h2" gutterBottom>
            <Trans i18nKey={paymentDialogState?.title} />
          </Typography>
        </>
      </DialogTitle>
      <DialogContent>
        <>
          <Typography variant="body1" component="p" gutterBottom style={{ paddingTop: '2rem' }}>
            <Trans i18nKey={paymentDialogState?.description} />
          </Typography>
          {hasPaymentPdf && (
            <Button
              id="download-proof-of-payment-success"
              variant="text"
              color="primary"
              aria-controls="simple-menu"
              onClick={handleDownloadProofOfPayment}
            >
              <GetApp color="primary" />
              <Trans
                i18nKey={
                  submission?.paymentResponse?.successful
                    ? 'actions.download.proof.of.payment.result'
                    : 'actions.download.failed.payment.result'
                }
              />
            </Button>
          )}
        </>
      </DialogContent>
      {(paymentDialogState === PaymentDialogState.PROCESSING ||
        paymentDialogState === PaymentDialogState.INITIALIZING) && (
        <DialogActions>
          <Loader size={75} />
        </DialogActions>
      )}
      {paymentDialogState === PaymentDialogState.PAYMENT_SUCCESS && (
        <DialogActions>
          <Button id="button-payment-preview" color="default" variant="contained" onClick={handlePaymentPreview}>
            {t('payment.preview')}
          </Button>

          <Button id="button-overview-page" color="primary" variant="contained" onClick={handleGoToOverview}>
            {t('overview.page.go')}
          </Button>
        </DialogActions>
      )}
      {(paymentDialogState === PaymentDialogState.PAYMENT_FAILED ||
        paymentDialogState === PaymentDialogState.PAYMENT_TIMEOUT ||
        paymentDialogState === PaymentDialogState.PRE_PAYMENT_FAILED) && (
        <DialogActions>
          <Button id="submission.payment.quit" color="default" variant="contained" onClick={handlePayLater}>
            {t('submission.payment.quit')}
          </Button>

          {/* Hide try button if it is not in ready to pay,
          it avoids retries when system fails to parse response after payment done */}
          {submission?.status === SubmissionStatus.READY_TO_PAY.id && (
            <Button id="button-try-again" color="primary" variant="contained" onClick={handleTryAgain}>
              {t('actions.try.again')}
            </Button>
          )}
        </DialogActions>
      )}
      {paymentDialogState === PaymentDialogState.CHANGE_PAYMENT_INTENT && (
        <DialogActions>
          <Button id="button-change-method" color="default" variant="contained" onClick={handleChangeMethodOfPayment}>
            {t('payment.change-method')}
          </Button>

          <Button id="button-pay-later" color="primary" variant="contained" onClick={handlePayLater}>
            {t('payment.pay-later')}
          </Button>
        </DialogActions>
      )}
      {paymentDialogState === PaymentDialogState.CHANGE_PAYMENT_INTENT_SUCCESS && (
        <DialogActions>
          <Button id="confirmStatus" color="primary" variant="contained" onClick={handleGoToOverview}>
            {t('overview.page.go')}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}
